import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { ApiClientError } from "@chaochud/api-client";
import type { AdminMfaSetupBeginResponseData } from "@chaochud/contracts";

import { Logo } from "@/components/logo";
import { adminAuthClient } from "@/lib/api-clients";
import { useAdminAuth } from "@/lib/auth/auth-context";

function toErrorMessage(error: unknown): string {
  if (error instanceof ApiClientError) {
    switch (error.code) {
      case "MFA_INVALID_CODE":
        return "รหัสยืนยันไม่ถูกต้อง";
      case "MFA_ALREADY_ENABLED":
        return "บัญชีนี้เปิด MFA แล้ว";
      case "MFA_TOKEN_INVALID":
        return "เซสชันตั้งค่าหมดอายุ กรุณาเข้าสู่ระบบใหม่";
      default:
        return error.message || "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง";
    }
  }
  return "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง";
}

export function MfaSetupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { completeSession, isAuthenticated, isHydrated } = useAdminAuth();
  const mfaToken = (location.state as { mfaToken?: string } | null)?.mfaToken;
  const [setup, setSetup] = useState<AdminMfaSetupBeginResponseData | null>(null);
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!mfaToken) return;
    let cancelled = false;
    void (async () => {
      try {
        const data = await adminAuthClient.beginMfaSetup(mfaToken);
        if (!cancelled) setSetup(data);
      } catch (error) {
        if (!cancelled) setServerError(toErrorMessage(error));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mfaToken]);

  if (isHydrated && isAuthenticated && !backupCodes) {
    return <Navigate to="/dashboard" replace />;
  }

  if (!mfaToken) {
    return <Navigate to="/login" replace />;
  }

  if (backupCodes) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-6 bg-slate-bg p-6">
        <div className="w-full max-w-md rounded-xl border border-slate-line bg-slate-surface p-6 shadow-sm">
          <h2 className="mb-2 font-heading text-lg font-semibold text-ink">บันทึกรหัสสำรอง</h2>
          <p className="mb-4 text-sm text-muted">
            เก็บรหัสเหล่านี้ไว้ในที่ปลอดภัย ใช้ได้ครั้งเดียวหากเข้าถึงแอป Authenticator ไม่ได้
          </p>
          <ul className="mb-4 grid grid-cols-2 gap-2 font-mono text-sm">
            {backupCodes.map((item) => (
              <li key={item} className="rounded bg-slate-row px-2 py-1 text-ink">
                {item}
              </li>
            ))}
          </ul>
          <button
            type="button"
            className="w-full rounded-lg bg-plum px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
            onClick={() => navigate("/dashboard")}
          >
            ไปที่แดชบอร์ด
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-6 bg-slate-bg p-6">
      <Logo iconSize={32} textClassName="text-2xl" />
      <div className="w-full max-w-md rounded-xl border border-slate-line bg-slate-surface p-6 shadow-sm">
        <h2 className="mb-2 font-heading text-lg font-semibold text-ink">เปิดใช้ MFA</h2>
        {loading ? <p className="text-sm text-muted">กำลังสร้างรหัส...</p> : null}
        {setup ? (
          <>
            <p className="mb-3 text-sm text-muted">
              สแกน QR ด้วยแอป Authenticator หรือใส่ secret ด้วยตนเอง
            </p>
            <img src={setup.qrCodeDataUrl} alt="MFA QR code" className="mx-auto mb-3 h-48 w-48" />
            <p className="mb-4 break-all font-mono text-xs text-muted">{setup.secret}</p>
            <form
              className="flex flex-col gap-3"
              onSubmit={async (event) => {
                event.preventDefault();
                setSubmitting(true);
                setServerError(null);
                try {
                  const result = await adminAuthClient.confirmMfaSetup({ mfaToken, code });
                  completeSession(result);
                  setBackupCodes(result.backupCodes);
                } catch (error) {
                  setServerError(toErrorMessage(error));
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              <input
                value={code}
                onChange={(event) => setCode(event.target.value)}
                className="rounded-lg border border-slate-line px-3 py-2 text-sm tracking-widest"
                placeholder="000000"
                inputMode="numeric"
                autoComplete="one-time-code"
              />
              {serverError ? <p className="text-sm text-error-text">{serverError}</p> : null}
              <button
                type="submit"
                disabled={submitting}
                className="rounded-lg bg-plum px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
              >
                ยืนยันและเปิดใช้ MFA
              </button>
            </form>
          </>
        ) : null}
        {!loading && serverError && !setup ? (
          <p className="text-sm text-error-text">{serverError}</p>
        ) : null}
      </div>
    </main>
  );
}
